import React from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import {
  type DealOptionGroup,
  type PackageRoleChoices,
  type PackageVariantError,
  roomsWithVariants,
} from '../tabs/packageOptions/previousPackageVariants';

import CopyPackageVariantsRow, { type PackageRoleChange } from './CopyPackageVariantsRow';
import { packageVariantErrorText } from './copyFromPreviousModel';

type CopyPackageVariantsStepProps = {
  choices: PackageRoleChoices;
  errors: Array<PackageVariantError>;
  groups: Array<DealOptionGroup>;
  onChange: PackageRoleChange;
};

function errorKey(error: PackageVariantError): string {
  switch (error.reason) {
    case 'noBase':
      return `noBase::${error.assetId}`;
    case 'overlappingStays':
      return `overlappingStays::${error.packageKey}`;
    case 'noSuffix':
      return `noSuffix::${error.groupKey}`;
  }
}

export default function CopyPackageVariantsStep({ choices, errors, groups, onChange }: CopyPackageVariantsStepProps) {
  const missingSuffix = new Set(errors.flatMap((error) => (error.reason === 'noSuffix' ? [error.groupKey] : [])));

  return (
    <>
      <Typography sx={{ mt: 2 }} variant="subtitle2">
        Package variants
      </Typography>
      <Typography color="text.secondary" variant="body2">
        Some rooms have more than one package in Salesforce. Check how each one copies to Package Options: as the Basic
        package, an Upgrade, a Solo traveller, or not at all.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Deal Option</TableCell>
            <TableCell>Stays</TableCell>
            <TableCell>Copy as</TableCell>
            <TableCell>Shows on Package Options as</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roomsWithVariants(groups).map((roomGroups) => (
            <React.Fragment key={roomGroups[0].asset.id}>
              <TableRow>
                <TableCell colSpan={4} sx={{ bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2">{roomGroups[0].asset.name}</Typography>
                </TableCell>
              </TableRow>
              {roomGroups.map((group) => (
                <CopyPackageVariantsRow
                  choice={choices[group.key]}
                  group={group}
                  key={group.key}
                  missingSuffix={missingSuffix.has(group.key)}
                  onChange={onChange}
                />
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      {errors.map((error) => (
        <Typography color="error" key={errorKey(error)} sx={{ mt: 1 }} variant="body2">
          {packageVariantErrorText(error)}
        </Typography>
      ))}
    </>
  );
}
